import { useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';
import {
    AdditionalUserInfoCard,
    AutoPresavedContainer,
    EmptyPresaves,
    FanInformationContainer,
    PresavesContainer,
    StyledLabel,
    TabsContainer,
    UserInformationContainer,
} from "./styles";
import { getFormattedLocation } from '../FansTable/utils';
import { getPresavedByFan_api } from 'api/fanApi';
import PresaveCard from './PresaveCard';
import PlatformCell from '../FansTable/PlatformCell';
import isEqual from "lodash/isEqual";
import { TabSelector } from 'helpers/TabSelector';
import { DefaultImage } from '../../styles';
import { getFormattedName, getFormattedDate, getFormattedSource } from './utils';
import emptyUser from "assets/images/user.svg";
import Sty, { StyledNavBar } from 'modules/Sty';
import useBrand from 'Hooks/useBrand';
import ModalSymphony from 'elements/ModalSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import TabSymphony from 'elements/TabSymphony';
import Loader from 'elements/Loader';
import useFans from 'Hooks/useFans';
import Colors from 'modules/Colors';

export type Presave = {
    contentName: string,
    artistName: string
    imageUrl: string,
    type: string,
}

enum Tabs {
    PRESAVES = "Pre-saved Releases",
}

const { PRESAVES } = Tabs;

const FansDetailsModal = () => {
    const { isProBrand } = useBrand();
    const {
        selectedFan,
        setShowFansDetailsModal,
        showFansDetailsModal
    } = useFans()

    const [presaves, setPresaves] = useState<Presave[]>([]);
    const [loadingPresaves, setLoadingPresaves] = useState<boolean>(false)
    const [loadingForeverPresaves, setLoadingForeverPresaves] = useState<boolean>(false)
    const [autoPresaves, setAutoPresaves] = useState<boolean>(selectedFan?.presaveFutureReleases || false)
    const [selectedTab, setSelectedTab] = useState<Tabs>(PRESAVES);

    const getPresaves = async (fanId: number) => {
        setLoadingPresaves(true)
        const { data, error } = await getPresavedByFan_api(fanId)
        if (error || !data) {
            setPresaves([])
        } else {
            setPresaves(data)
        }
        setLoadingPresaves(false)
    }

    useEffect(() => {
        if (selectedFan?.id) {
            setAutoPresaves(selectedFan.presaveFutureReleases)
        }
    }, [selectedFan])

    useEffect(() => {
        setLoadingPresaves(true)
        if (selectedFan?.id && isEqual(selectedTab, PRESAVES)) {
            setPresaves([])
            getPresaves(selectedFan.id)
        }
    }, [selectedFan, selectedTab])

    if (!selectedFan) {
        return null
    }

    const fromPresave = selectedFan.origin === "presave";

    const renderForeverSavesStatus = () => {
        let element = null;

        if (loadingForeverPresaves) {
            element = (
                <Loader
                    size={58}
                />
            )
        } else {

            let fanEnabledForeverSaves = autoPresaves
            let titleMessage = fanEnabledForeverSaves ? "Subscribed to Forever Saves" : ""
            let descriptionMsg = fanEnabledForeverSaves ? "This fan is subscribed to saving all of your future releases. You can set their subscription status at the link below - if they're unsubscribed, they'll be able to re-subscribe from any future presave campaign." : "This fan hasn't consented or subscribed to Forever Saves. They'll be able to re-subscribe from any future presave campaign."
            let fanAction = fanEnabledForeverSaves && selectedFan.updateSubscriptionLink && (
                <ButtonSymphony
                    className="editSubscriptionStatusButton"
                    variant="text"
                    onClick={() => {
                        window.open(selectedFan.updateSubscriptionLink, "_blank")
                    }}
                >
                    Edit Subscription Status
                </ButtonSymphony>
            )

            element = (
                <AdditionalUserInfoCard>
                    <StyledLabel fontSize="16px">{titleMessage}</StyledLabel>
                    <StyledLabel color={Colors.greyDark} fontSize="12px">
                        {descriptionMsg}
                    </StyledLabel>
                    {fanAction}
                </AdditionalUserInfoCard>
            )
        }

        return (
            <AutoPresavedContainer style={{ marginTop: 24, marginBottom: -16 }}>
                {element}
            </AutoPresavedContainer>
        )
    }

    return (
        <ModalSymphony
            className="fansDetailsModalComponent"
            isOpen={showFansDetailsModal}
            onClose={() => setShowFansDetailsModal(false)}
            headerIcon={
                <DefaultImage
                    className="fansDetailsModalHeaderIcon"
                    borderRadius="50%"
                    height="56px"
                    width="56px"
                    src={selectedFan.profile_url || emptyUser}
                    alt="user avatar"
                    backgroundColor={selectedFan.profile_url ? "none" : Colors.greyLight}
                />
            }
            title={
                <UserInformationContainer>
                    {fromPresave &&
                        <PlatformCell
                            className="fanDetailsPlatformCell"
                            platformKey={selectedFan.platform}
                        />
                    }
                    <StyledLabel fontSize="32px" blur={!isProBrand} style={{ marginTop: 16 }}>
                        {getFormattedName({
                            name: selectedFan.name,
                            lastname: selectedFan.lastname,
                            isProBrand: isProBrand
                        })}
                    </StyledLabel>
                </UserInformationContainer>
            }
            subtitle={selectedFan.email || selectedFan.cellphone || "Contact info not shared"}
        >
            <AdditionalUserInfoCard>
                <StyledLabel color={Colors.greyDark} fontSize="16px">
                    Joined
                </StyledLabel>
                <StyledLabel fontSize="16px">
                    {getFormattedDate(selectedFan.createdAt)}
                </StyledLabel>
            </AdditionalUserInfoCard>
            <AdditionalUserInfoCard>
                <StyledLabel color={Colors.greyDark} fontSize="16px">
                    Contact Info - Email
                </StyledLabel>
                <StyledLabel fontSize="16px">
                    {selectedFan.email || "Email not collected"}
                </StyledLabel>
            </AdditionalUserInfoCard>
            {selectedFan.cellphone && (<AdditionalUserInfoCard>
                <StyledLabel color={Colors.greyDark} fontSize="16px">
                    Contact Info - Number
                </StyledLabel>
                <StyledLabel fontSize="16px">
                    {selectedFan.cellphone}
                </StyledLabel>
            </AdditionalUserInfoCard>)}
            <AdditionalUserInfoCard>
                <StyledLabel color={Colors.greyDark} fontSize="16px">
                    Location
                </StyledLabel>
                <StyledLabel fontSize="16px" blur={!isProBrand}>
                    {getFormattedLocation({ city: selectedFan.city, country: selectedFan.country, isProBrand })}
                </StyledLabel>
            </AdditionalUserInfoCard>
            <AdditionalUserInfoCard>
                <StyledLabel color={Colors.greyDark} fontSize="16px">
                    Source
                </StyledLabel>
                <StyledLabel fontSize="16px">
                    {getFormattedSource(selectedFan.origin, presaves[0]?.contentName || "")}
                </StyledLabel>
                {fromPresave && renderForeverSavesStatus()}
            </AdditionalUserInfoCard>
            {fromPresave &&
                <TabsContainer style={{ paddingTop: 0 }}>
                    <StyledNavBar>
                        <TabSelector
                            isActive={isEqual(selectedTab, PRESAVES)}
                            onClick={() => setSelectedTab(PRESAVES)}
                        >
                            {PRESAVES}
                        </TabSelector>
                    </StyledNavBar>
                    <TabSymphony className="presavesTab" visible={isEqual(selectedTab, PRESAVES)}>
                        {presaves.length ? (
                            <PresavesContainer>
                                {presaves.map((presave: Presave) => (
                                    <PresaveCard
                                        key={`${presave.artistName}-${presave.contentName}`}
                                        artistName={presave.artistName}
                                        imageUrl={presave.imageUrl}
                                        presaveName={presave.contentName}
                                        presaveType={presave.type}
                                    />
                                ))}
                            </PresavesContainer>
                        ) : loadingPresaves ? (
                            <Grid className="w-full flex justify-center">
                                <Loader className="loaderFansDetailsModalLoader" />
                            </Grid>
                        ) : (
                            <EmptyPresaves> This fan hasn’t pre-saved any releases yet.</EmptyPresaves>
                        )}
                    </TabSymphony>
                </TabsContainer>
            }
        </ModalSymphony>
    );
}

export default FansDetailsModal;
